import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Heart, Thermometer, Activity, AlertTriangle } from 'lucide-react';

interface VitalSigns {
  timestamp: string;
  heartRate: number;
  bloodPressureSystolic: number;
  bloodPressureDiastolic: number;
  temperature: number;
  restingHeartRate: number;
}

const HealthMetrics: React.FC = () => {
  // Mock data - in real app this would come from various health sensors
  const vitalSignsData: VitalSigns[] = [
    { timestamp: '12AM', heartRate: 68, bloodPressureSystolic: 122, bloodPressureDiastolic: 78, temperature: 98.2, restingHeartRate: 65 },
    { timestamp: '3AM', heartRate: 62, bloodPressureSystolic: 118, bloodPressureDiastolic: 75, temperature: 98.0, restingHeartRate: 62 },
    { timestamp: '6AM', heartRate: 70, bloodPressureSystolic: 120, bloodPressureDiastolic: 76, temperature: 98.1, restingHeartRate: 64 },
    { timestamp: '9AM', heartRate: 75, bloodPressureSystolic: 125, bloodPressureDiastolic: 80, temperature: 98.4, restingHeartRate: 66 },
    { timestamp: '12PM', heartRate: 78, bloodPressureSystolic: 128, bloodPressureDiastolic: 82, temperature: 98.6, restingHeartRate: 68 },
    { timestamp: '3PM', heartRate: 76, bloodPressureSystolic: 126, bloodPressureDiastolic: 80, temperature: 98.5, restingHeartRate: 67 },
    { timestamp: '6PM', heartRate: 74, bloodPressureSystolic: 124, bloodPressureDiastolic: 79, temperature: 98.4, restingHeartRate: 66 },
    { timestamp: '9PM', heartRate: 72, bloodPressureSystolic: 122, bloodPressureDiastolic: 77, temperature: 98.3, restingHeartRate: 65 },
  ];

  const currentMetrics = {
    heartRate: 72,
    bloodPressure: '122/77',
    temperature: 98.3,
    restingHeartRate: 65,
    trends: {
      heartRate: '-2%',
      bloodPressure: 'stable',
      temperature: 'normal',
      restingHeartRate: 'optimal'
    }
  };

  const getStatusColor = (metric: string, value: number) => {
    switch (metric) {
      case 'heartRate':
        return value > 100 || value < 60 ? 'text-red-500' : 'text-green-500';
      case 'temperature':
        return value > 99.5 || value < 97.8 ? 'text-red-500' : 'text-green-500';
      default:
        return 'text-green-500';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Health Metrics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Heart className="text-red-500 mr-2" />
            <h3 className="text-lg font-semibold">Heart Rate</h3>
          </div>
          <p className={`text-3xl font-bold ${getStatusColor('heartRate', currentMetrics.heartRate)}`}>
            {currentMetrics.heartRate}
          </p>
          <p className="text-gray-500">BPM</p>
          <p className="text-sm text-gray-400">Trend: {currentMetrics.trends.heartRate}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Blood Pressure</h3>
          </div>
          <p className="text-3xl font-bold">{currentMetrics.bloodPressure}</p>
          <p className="text-gray-500">mmHg</p>
          <p className="text-sm text-gray-400">Status: {currentMetrics.trends.bloodPressure}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Thermometer className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Temperature</h3>
          </div>
          <p className={`text-3xl font-bold ${getStatusColor('temperature', currentMetrics.temperature)}`}>
            {currentMetrics.temperature}°F
          </p>
          <p className="text-sm text-gray-400">Status: {currentMetrics.trends.temperature}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Heart className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Resting Heart Rate</h3>
          </div>
          <p className="text-3xl font-bold">{currentMetrics.restingHeartRate}</p>
          <p className="text-gray-500">BPM</p>
          <p className="text-sm text-gray-400">Status: {currentMetrics.trends.restingHeartRate}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">24-Hour Vital Signs Trend</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={vitalSignsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="heartRate" 
                stroke="#ef4444" 
                name="Heart Rate"
                strokeWidth={2}
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="bloodPressureSystolic" 
                stroke="#3b82f6" 
                name="Systolic BP"
                strokeWidth={2}
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="bloodPressureDiastolic" 
                stroke="#60a5fa" 
                name="Diastolic BP"
                strokeWidth={2}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="temperature" 
                stroke="#f97316" 
                name="Temperature"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Health Insights</h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Heart className="text-green-500" />
            </div>
            <div>
              <h4 className="font-medium">Stable Heart Rate</h4>
              <p className="text-gray-600">Heart rate remains within normal range with healthy variation throughout the day.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Activity className="text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium">Blood Pressure Trends</h4>
              <p className="text-gray-600">Blood pressure shows normal circadian rhythm with expected morning elevation.</p>
            </div>
          </div>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="text-yellow-500" />
            </div>
            <div>
              <h4 className="font-medium">Recommendations</h4>
              <p className="text-gray-600">Consider morning medication timing to better manage blood pressure peaks.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthMetrics; 